import { Methods } from '../../api/methods.js';
import type { ParsedArgs } from '../args.js';
import { booleanFlag, flag, flagAll, numberFlag } from '../args.js';
import { CliError, ExitCode } from '../errors.js';
import type { CommandResult } from '../output.js';
import { parseJson, parseJsonObject, pickDefined, request } from './common.js';

export async function settingsCommand(args: ParsedArgs): Promise<CommandResult> {
  const action = args.positionals[1] ?? 'get';
  switch (action) {
    case 'get':
      return request(args, Methods.Settings);
    case 'update': {
      const params = updateParams(args);
      if (!params || Object.keys(params).length === 0) {
        throw new CliError('settings update requires at least one field', ExitCode.Usage);
      }
      return request(args, Methods.SettingsUpdate, params);
    }
    case 'reload':
      return request(args, Methods.SettingsReload);
    default:
      throw new CliError(`Unknown settings action "${action}"`, ExitCode.Usage);
  }
}

function updateParams(args: ParsedArgs): Record<string, unknown> | undefined {
  const raw = flag(args.flags, 'params');
  if (raw) return parseJsonObject(raw, '--params');
  return pickDefined({
    runZapScript: booleanFlag(args.flags, 'run-zapscript'),
    debugLogging: booleanFlag(args.flags, 'debug-logging'),
    audioScanFeedback: booleanFlag(args.flags, 'audio-scan-feedback'),
    readersAutoDetect: booleanFlag(args.flags, 'readers-auto-detect'),
    errorReporting: booleanFlag(args.flags, 'error-reporting'),
    encryption: booleanFlag(args.flags, 'encryption'),
    backupRemoteEnabled: booleanFlag(args.flags, 'backup-remote-enabled'),
    playtimeSyncEnabled: booleanFlag(args.flags, 'playtime-sync-enabled'),
    updateChannel: flag(args.flags, 'update-channel'),
    backupRemoteSchedule: flag(args.flags, 'backup-remote-schedule'),
    readersScanMode: flag(args.flags, 'readers-scan-mode'),
    readersScanExitDelay: numberFlag(args.flags, 'readers-scan-exit-delay'),
    readersScanIgnoreSystems: optionalMany(args, 'readers-scan-ignore-system'),
    readersConnect: jsonFlag(args, 'readers-connect'),
    systemDefaults: jsonFlag(args, 'system-defaults'),
    audioVolume: numberFlag(args.flags, 'audio-volume'),
    launchGuardEnabled: booleanFlag(args.flags, 'launch-guard-enabled'),
    launchGuardTimeout: numberFlag(args.flags, 'launch-guard-timeout'),
    launchGuardDelay: numberFlag(args.flags, 'launch-guard-delay'),
    launchGuardRequireConfirm: booleanFlag(args.flags, 'launch-guard-require-confirm'),
    profilesRequireForLaunch: booleanFlag(args.flags, 'profiles-require-for-launch'),
    profilesSwapData: booleanFlag(args.flags, 'profiles-swap-data'),
  });
}

function optionalMany(args: ParsedArgs, name: string): string[] | undefined {
  const values = flagAll(args.flags, name);
  return values.length > 0 ? values : undefined;
}

function jsonFlag(args: ParsedArgs, name: string): unknown {
  const raw = flag(args.flags, name);
  return raw ? parseJson(raw, `--${name}`) : undefined;
}
